import classNames from 'classnames';
import { ReactNode, useMemo, useState, type FC } from 'react';
import type { FileUploadedListProps } from './FileUploaded.types';
import FileUploadedItem from './FileUploadedItem.component';
import {
  FileUploadState,
  FileUploadStatus,
} from 'hooks/filesUploadStoreFactory';
import { Button } from 'components/atoms/Button';
import { ChevronDownIcon, ChevronUpIcon } from 'utils/icons/figmaIcons';
import { useTranslation } from 'react-i18next';

const FilesGroup: FC<{
  label: string;
  files: FileUploadState[];
  renderFile: (fileState: FileUploadState) => ReactNode;
}> = ({ label, renderFile, files }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!files.length) return null;

  return (
    <>
      <Button
        asDefault
        className="w-full flex items-center text-primary-400"
        onClick={() => setIsCollapsed((prev) => !prev)}
      >
        <span>
          {label} ({files.length})
        </span>
        <span className="h-px flex-1 bg-primary-400 ml-1" />
        {isCollapsed ? (
          <ChevronDownIcon className="w-6 h-6" />
        ) : (
          <ChevronUpIcon className="w-6 h-6" />
        )}
      </Button>
      {!isCollapsed && files.map(renderFile)}
    </>
  );
};

const FileUploadedList: FC<FileUploadedListProps> = ({
  files,
  className,
  onRetry,
  onFileReview,
  color,
  renderUploadedFileItem,
  hideProgressBarOnDone,
  groupDoneFiles,
  groupFailedFiles,
}) => {
  const { t } = useTranslation();

  const grouped = useMemo(() => {
    const groups = {
      done: [] as FileUploadState[],
      failed: [] as FileUploadState[],
      other: [] as FileUploadState[],
    };
    files.forEach((fileState) => {
      const isDone = fileState.status === FileUploadStatus.done;
      const isFailed = fileState.status === FileUploadStatus.error;
      if (groupDoneFiles && isDone) return groups.done.push(fileState);
      if (groupFailedFiles && isFailed) return groups.failed.push(fileState);
      groups.other.push(fileState);
    });
    return groups;
  }, [groupDoneFiles, groupFailedFiles, files]);

  const renderFile = (fileState: FileUploadState) => {
    const { key, file, status, progress, options } = fileState;
    if (renderUploadedFileItem) return renderUploadedFileItem(fileState, files);
    return (
      <FileUploadedItem
        key={key}
        color={color}
        status={status}
        name={file.name}
        size={file.size}
        progress={progress}
        hideProgressBarOnDone={hideProgressBarOnDone}
        onRetry={() => onRetry({ file, key, options })}
        onReview={onFileReview ? () => onFileReview(fileState) : undefined}
      />
    );
  };

  return (
    <div className={classNames(className)}>
      {grouped.other.map(renderFile)}
      <FilesGroup
        label={t('dropzone.list_group.failed')}
        files={grouped.failed}
        renderFile={renderFile}
      />
      <FilesGroup
        label={t('dropzone.list_group.done')}
        files={grouped.done}
        renderFile={renderFile}
      />
    </div>
  );
};

export default FileUploadedList;
